import { useEffect, useState } from 'react';
import { Link2, Copy, Trash2, Eye, Clock, Check } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';
import { useShareStore } from '@/stores/shareStore';
import { Button } from '@/components/ui/button';
import { CreateShareLinkModal } from './CreateShareLinkModal';

interface Props {
  propertyId: string;
  isOwner: boolean;
}

export function ActiveShareLinks({ propertyId, isOwner }: Props) {
  const { links, fetchLinks, deleteLink } = useShareStore();
  const [showModal, setShowModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const propertyLinks = links[propertyId] || [];

  useEffect(() => {
    if (isOwner) {
      fetchLinks(propertyId);
    }
  }, [propertyId, isOwner, fetchLinks]);

  if (!isOwner) return null;

  const handleCopy = async (token: string, id: string) => {
    const url = `${window.location.origin}/s/${token}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success('Link kopiert');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteLink(id, propertyId);
      toast.success('Link gelöscht');
    } catch {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-foreground-muted" />
          <h3 className="font-medium">Freigabe-Links</h3>
          {propertyLinks.length > 0 && (
            <span className="text-xs text-foreground-muted bg-background-subtle px-1.5 py-0.5 rounded-full">
              {propertyLinks.length}
            </span>
          )}
        </div>
        <Button size="sm" variant="secondary" onClick={() => setShowModal(true)}>
          Link erstellen
        </Button>
      </div>

      {propertyLinks.length === 0 ? (
        <div className="text-center py-6 text-foreground-muted text-sm border border-dashed border-border rounded-xl">
          <Link2 className="w-5 h-5 mx-auto mb-2 opacity-40" />
          Noch keine Links erstellt
        </div>
      ) : (
        <div className="space-y-2">
          {propertyLinks.map((link) => {
            const isExpired = link.expires_at ? new Date(link.expires_at) < new Date() : false;
            return (
              <div
                key={link.id}
                className={`flex items-start gap-3 p-3 rounded-xl border ${
                  isExpired ? 'border-border opacity-60' : 'border-border bg-background-subtle'
                }`}
              >
                <div className="text-lg shrink-0">
                  {link.profile?.icon || '🔗'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-sm font-medium">
                      {link.name || link.profile?.name || 'Unbenannter Link'}
                    </p>
                    {isExpired && (
                      <span className="text-xs text-negative">abgelaufen</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="text-xs text-foreground-muted flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {link.view_count} Aufrufe
                    </span>
                    <span className="text-xs text-foreground-muted">
                      {formatDate(link.created_at)}
                    </span>
                    {link.expires_at && (
                      <span className="text-xs text-foreground-muted flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        bis {formatDate(link.expires_at)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleCopy(link.token, link.id)}
                    className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-background-elevated"
                    title="Link kopieren"
                  >
                    {copiedId === link.id ? (
                      <Check className="w-3.5 h-3.5 text-accent-green" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(link.id)}
                    disabled={deletingId === link.id}
                    className="p-1.5 rounded-lg text-foreground-muted hover:text-negative hover:bg-negative/10 disabled:opacity-50"
                    title="Link löschen"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <CreateShareLinkModal
          propertyId={propertyId}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
